import { useEffect, useMemo, useState } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Organization } from '@medplum/fhirtypes';

import { usePortalSubject } from '../../../../contexts/PortalSubjectContext';
import { getPracticeOrganizationId, resolvePracticeOrganization } from '../../../../lib/practiceOrganization';

export interface UsePracticeOrganizationResult {
  organization: Organization | null;
  organizationId: string | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function usePracticeOrganization(): UsePracticeOrganizationResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { activePatientId } = usePortalSubject();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);

  const refetch = useMemo(() => () => setRefetchCounter((c) => c + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const org = await resolvePracticeOrganization(medplum, profile ?? null, {
          portalPatientId: activePatientId,
        });
        if (!cancelled) setOrganization(org);
      } catch (err) {
        if (!cancelled) {
          setOrganization(null);
          setError(err instanceof Error ? err : new Error('Failed to load practice organization'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, profile, activePatientId, refetchCounter]);

  return {
    organization,
    organizationId: getPracticeOrganizationId(organization),
    loading,
    error,
    refetch,
  };
}
